// API service for recording portal

const API_BASE_URL = 'https://d6kirim839.execute-api.eu-west-2.amazonaws.com/prod';

function portalHeaders(accessToken, extra = {}) {
  const headers = { ...extra };
  if (accessToken) {
    headers['X-Portal-Token'] = accessToken;
  }
  return headers;
}

/**
 * Fetch a recording project by slug
 * @param {string} slug - The project slug (e.g., "bassoon-session")
 * @param {string|null} accessToken - Magic-link token (required for new portals)
 * @returns {Promise<Object>} - The project data
 */
export async function fetchProject(slug, accessToken = null) {
  const response = await fetch(`${API_BASE_URL}/project/${encodeURIComponent(slug)}`, {
    headers: portalHeaders(accessToken),
  });
  
  if (response.status === 404) {
    return null;
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch project: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get a pre-signed URL for uploading a file
 * @param {string} slug - The project slug
 * @param {string} deliverableId - The deliverable ID
 * @param {string} filename - The filename to upload
 * @param {string|null} accessToken - Magic-link token
 * @returns {Promise<{uploadUrl: string, fileKey: string}>}
 */
export async function getUploadUrl(
  slug,
  deliverableId,
  filename,
  accessToken = null,
  fileSize = null,
) {
  const body = { slug, deliverableId, filename };
  if (accessToken) {
    body.accessToken = accessToken;
  }
  if (fileSize != null) {
    body.fileSize = fileSize;
  }

  const response = await fetch(`${API_BASE_URL}/upload-url`, {
    method: 'POST',
    headers: portalHeaders(accessToken, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to get upload URL: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Upload a file directly to S3 using a pre-signed URL
 * @param {string} uploadUrl - The pre-signed S3 URL
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback (0-100)
 * @returns {Promise<void>}
 */
export async function uploadFileToS3(uploadUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: network error'));
    });
    
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', 'audio/wav');
    xhr.send(file);
  });
}

/**
 * Update deliverable status after successful upload
 * @param {string} slug - The project slug
 * @param {string} deliverableId - The deliverable ID
 * @param {string} fileKey - The S3 file key
 * @param {string} fileHash - Optional SHA-256 hash for duplicate detection
 * @param {string|null} accessToken - Magic-link token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function updateDeliverableStatus(
  slug,
  deliverableId,
  fileKey,
  fileHash = null,
  accessToken = null,
) {
  const body = { slug, deliverableId, fileKey };
  if (fileHash) {
    body.fileHash = fileHash;
  }
  if (accessToken) {
    body.accessToken = accessToken;
  }
  
  const response = await fetch(`${API_BASE_URL}/deliverable/status`, {
    method: 'POST',
    headers: portalHeaders(accessToken, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to update status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Complete upload flow: get URL, upload file, update status
 * @param {string} slug - The project slug
 * @param {string} deliverableId - The deliverable ID
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback (0-100)
 * @param {string} fileHash - Optional SHA-256 hash for duplicate detection
 * @param {string|null} accessToken - Magic-link token
 * @returns {Promise<{success: boolean}>}
 */
export async function uploadRecording(
  slug,
  deliverableId,
  file,
  onProgress,
  fileHash = null,
  accessToken = null,
) {
  // Step 1: Get pre-signed URL (fileSize required server-side for 2.5GB cap)
  const { uploadUrl, fileKey } = await getUploadUrl(
    slug,
    deliverableId,
    file.name,
    accessToken,
    file.size,
  );
  
  // Step 2: Upload file to S3
  await uploadFileToS3(uploadUrl, file, onProgress);
  
  // Step 3: Update status in DynamoDB (include file hash for duplicate detection)
  await updateDeliverableStatus(slug, deliverableId, fileKey, fileHash, accessToken);
  
  return { success: true };
}
